import TextInput from '../../components/ui/TextInput'
import HTMLReactParser from 'html-react-parser'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { authService } from '../../services/auth.service'
import { SplitScreenContext } from '../../layouts/SplitScreenLayout'
import { googleOAuthClientId } from '../../config'

type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string
        callback: (response: { credential: string }) => void
        auto_select?: boolean
      }) => void
      renderButton: (
        parent: HTMLElement,
        options: {
          theme?: string
          size?: string
          width?: number
          text?: string
          locale?: string
          shape?: string
        },
      ) => void
    }
  }
}

const GOOGLE_GIS_SCRIPT_ID = 'google-gsi-client'
const GOOGLE_GIS_SRC = 'https://accounts.google.com/gsi/client'

function getGoogleGis(): GoogleIdentityServices | undefined {
  const google = (window as any).google as GoogleIdentityServices | undefined
  return google?.accounts?.id ? google : undefined
}

function loadGoogleGisScript(): Promise<void> {
  if (getGoogleGis()?.accounts?.id) {
    return Promise.resolve()
  }

  const existing = document.getElementById(GOOGLE_GIS_SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Sign-In')))
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = GOOGLE_GIS_SCRIPT_ID
    script.src = GOOGLE_GIS_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google Sign-In'))
    document.head.appendChild(script)
  })
}

const GoogleMark = () => (
  <svg className="login-google-mark" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
)

const LoginPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [isPasswordResetLinkVisible, setIsPasswordResetLinkVisible] = useState(false)
  const googleButtonRef = useRef<HTMLDivElement>(null)
  const googleOverlayRef = useRef<HTMLDivElement>(null)

  const completeLogin = useCallback(
    (response: { account: { role: string } }) => {
      const redirectTo = searchParams.get('redirectTo')
      if (redirectTo) {
        navigate(redirectTo)
      } else {
        navigate(`/${response.account.role.toLowerCase()}/dashboard`)
      }
    },
    [navigate, searchParams],
  )

  const handleGoogleCredential = useCallback(
    async (credential: string) => {
      setError('')
      setLoading(true)
      try {
        const response = await authService.loginWithGoogle(credential)
        completeLogin(response)
      } catch (err) {
        setIsPasswordResetLinkVisible(true)
        console.error('Login: Error during Google login:', err)
        setError(err instanceof Error ? err.message : 'Google login failed')
      } finally {
        setLoading(false)
      }
    },
    [completeLogin],
  )

  useEffect(() => {
    const errorParam = searchParams.get('error')
    if (errorParam) {
      setError(errorParam)
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('error')
      setSearchParams(newSearchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!googleOAuthClientId || !googleOverlayRef.current) {
      return
    }

    let cancelled = false

    const renderGoogleButton = () => {
      const googleGis = getGoogleGis()
      const host = googleOverlayRef.current
      if (cancelled || !host || !googleGis?.accounts?.id) {
        return
      }

      googleGis.accounts.id.initialize({
        client_id: googleOAuthClientId,
        callback: (response) => {
          if (response.credential) {
            void handleGoogleCredential(response.credential)
          }
        },
        auto_select: false,
      })

      const width = Math.max(googleButtonRef.current?.offsetWidth || 320, 280)
      host.innerHTML = ''
      googleGis.accounts.id.renderButton(host, {
        theme: 'outline',
        size: 'large',
        width,
        text: 'signin_with',
        locale: 'en',
        shape: 'rectangular',
      })
    }

    loadGoogleGisScript()
      .then(renderGoogleButton)
      .catch((err) => {
        console.error('Google Sign-In failed to initialize:', err)
      })

    const onResize = () => renderGoogleButton()
    window.addEventListener('resize', onResize)

    return () => {
      cancelled = true
      window.removeEventListener('resize', onResize)
    }
  }, [handleGoogleCredential])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await authService.login(email, password)
      completeLogin(response)
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError(
          `The request could not be completed, the backend API may not be configured correctly.`
        )
        return
      }
      setIsPasswordResetLinkVisible(true)
      console.error('Login: Error during login:', err)
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-form">
      <div className="error-message">{HTMLReactParser(error)}</div>
      <div className={`muted request-password-reset ${isPasswordResetLinkVisible ? '' : 'hidden'}`}>
        {`Forgot your password?`}{' '}
        <Link
          className="simple-link"
          to={`/request-password-reset?email=${encodeURIComponent(email)}`}
        >
          {`Click here to request a password reset!`}
        </Link>
      </div>
      <form onSubmit={handleSubmit}>
        <TextInput
          required={true}
          type="text"
          label={`Email address`}
          name="email"
          value={email}
          onChange={(value: string) => setEmail(value)}
        ></TextInput>
        <TextInput
          required={true}
          type="password"
          label={`Password`}
          name="password"
          value={password}
          onChange={(value: string) => setPassword(value)}
        ></TextInput>
        <div className="login-actions">
          <button type="submit" className="styled-button login-primary-button" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
          {googleOAuthClientId ? (
            <div className="login-google-wrap" ref={googleButtonRef}>
              <button type="button" className="styled-button login-google-button" disabled={loading} tabIndex={-1}>
                <GoogleMark />
                <span>Sign in with Google</span>
              </button>
              {/* Transparent GIS button on top receives the real click (iframe). */}
              <div className="login-google-overlay" ref={googleOverlayRef} aria-hidden="true" />
            </div>
          ) : null}
        </div>
      </form>
    </div>
  )
}

export const loginContext: SplitScreenContext = {
  id: 'login',
  title: 'Welcome <em>back</em>.',
  message: 'Log in with your email and password or continue with Google.',
  contentSide: 'right',
  logoSrc: '/images/landing-v2/logo.png',
  variant: 'v2',
}

export default LoginPage
